// File: src/pages/StaffDirectoryPage.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useNavigate } from 'react-router-dom';

const StaffDirectoryPage = () => {
  const [staff, setStaff] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [filter, setFilter] = useState('All');

  const { canViewStaffDirectory } = useUserPermissions();
  const navigate = useNavigate();

  const departments = ['All', 'Culinary', 'Bar', 'Front of House', 'Purchasing', 'Finance'];

  useEffect(() => {
    if (!canViewStaffDirectory) {
      navigate('/unauthorized');
    }
  }, [canViewStaffDirectory, navigate]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get('/solo-mdsh-api/staffing/staff-directory');
        setStaff(res.data?.staff || []);
      } catch (err) {
        console.error('❌ Error loading staff:', err);
      }
    };

    const checkLock = async () => {
      const status = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(status, ['cyberattack', 'recovery']));
    };

    fetchStaff();
    checkLock();
  }, []);

  const filtered = staff.filter((s) =>
    filter === 'All' ? true : s.department === filter || s.department_name === filter
  );

  return (
    <div className="bg-neutral-900 min-h-screen p-6 text-white">
      <h2 className="text-2xl font-bold mb-6">👥 Staff Directory</h2>

      <div className="mb-4 flex items-center gap-4">
        <label className="font-medium">Filter by Department:</label>
        <select
          className="bg-neutral-800 text-white p-2 rounded"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {departments.map((dept, i) => (
            <option key={i} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-auto rounded-xl border border-neutral-700">
        <table className="w-full text-sm">
          <thead className="bg-neutral-800 text-slate-300 text-left uppercase">
            <tr>
              <th className="p-3">Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Employment Type</th>
              <th>Email</th>
              <th>Pay Rate</th>
              <th>Pay Grade</th>
              <th className="text-right pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((person) => (
              <tr
                key={person.staff_id}
                className="border-t border-neutral-700 hover:bg-neutral-800 transition"
              >
                <td className="p-3 font-medium">{person.full_name}</td>
                <td>{person.position || '-'}</td>
                <td>{person.department || person.department_name || '-'}</td>
                <td>{person.employment_type || '-'}</td>
                <td>{person.email || '-'}</td>
                <td>£{person.pay_rate?.toFixed(2) || '0.00'}</td>
                <td>{person.pay_grade_status || '-'}</td>
                <td className="text-right pr-4">
                  {isLocked ? (
                    <span className="text-red-400">🚫 Locked</span>
                  ) : (
                    <button className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded">
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="8" className="p-4 text-center text-slate-400">
                  No staff members found for this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffDirectoryPage;
